<script setup lang="ts">
import type { VideoCardProps } from '.';

import { useEvents } from '~/composables/events/useEvents';
import { usePropertyIntentPrefetch } from '~/composables/shorts/usePropertyIntentPrefetch';
import { useToggleFavoriteSync } from '~/composables/useToggleFavoriteSync';
import { warmVideoManifestOnHover } from '~/composables/useVideoWarmup';
import { MixpanelProps } from '~/core/service/mixpanel';
import { hasShortsVideo } from '~/utils';

import IconMaterialSymbolsLightPlayArrow from '~icons/material-symbols-light/play-arrow';

import {
	REEL_THUMB_FORMAT,
	REEL_THUMB_HEIGHT,
	REEL_THUMB_QUALITY,
	REEL_THUMB_SIZES,
	REEL_THUMB_WIDTH
} from './reelThumbnailPreset';
import { usePropertyInfo } from './usePropertyInfo';
import { useVideoCardPlayer } from './useVideoCardPlayer';
import VideoCardFooter from './VideoCardFooter.vue';
import VideoCardHeader from './VideoCardHeader.vue';
import VideoCardLoadingBar from './VideoCardLoadingBar.vue';

const MOCK_AGENT = {
	name: 'Maria Silva',
	profilePictureUrl: 'https://i.pravatar.cc/150?u=broker-mock'
};

const MOCK_COMPANY = {
	name: 'Pilar Imobiliária',
	logoUrl: 'https://i.pravatar.cc/150?u=company-mock'
};

const textProtectionOverlayStyle = {
	background:
		'linear-gradient(180deg, rgba(0, 0, 0, 0.3) 11.48%, rgba(0, 0, 0, 0) 31.75%, rgba(0, 0, 0, 0) 58.79%, rgba(0, 0, 0, 0.3) 79.07%)'
};

const props = withDefaults(defineProps<VideoCardProps>(), {
	isActive: false,
	hoverToPlay: true,
	preloadWhenVisible: false,
	imgEagerCount: 3,
	imgPreloadCount: 0,
	playerRootMargin: '100px',
	sectionName: 'shorts'
});

const emit = defineEmits<{
	click: [index: number];
	/** Fired once after 2s of continuously watched preview — a no-touch
	 *  intent signal (the only one available on mobile autoplay). */
	engaged: [index: number];
}>();

const route = useRoute();

const ENGAGED_PREVIEW_MS = 2000;
let engagedTimer: ReturnType<typeof setTimeout> | undefined;
let hasEmittedEngaged = false;
const property = computed(() => props.property);

const {
	cardRef,
	videoRef,
	isVideoPlaying,
	isVisible,
	isLoading,
	shouldPlay,
	videoProgress,
	isMuted,
	handleMuteToggle,
	seekTo
} = useVideoCardPlayer({
	videoUrl: computed(() => props.videoUrl),
	isActive: computed(() => props.isActive),
	hoverToPlay: computed(() => props.hoverToPlay),
	preloadWhenVisible: computed(() => props.preloadWhenVisible),
	index: computed(() => props.index),
	playerRootMargin: props.playerRootMargin
});

const {
	formattedPrice,
	formattedArea,
	roomsInfo,
	propertyLocation,
	cityState
} = usePropertyInfo(property);

const propertyId = computed(() => props.property.id);
const commercialId = computed(() => props.property.commercialId);

const { prefetchPropertyOnIntent } = usePropertyIntentPrefetch();

// Hover = likely navigation into this card's reel: warm the video manifest
// and start the property request the reel page will need.
function handlePointerEnter() {
	warmVideoManifestOnHover(props.videoUrl);
	prefetchPropertyOnIntent(commercialId.value);
}
const sectionName = toRef(props, 'sectionName');

const favoriteEventData = computed(() => ({
	currentPage: route.fullPath,
	companyName: props.property.company?.name,
	agentName: props.property.agent?.name,
	tagExclusive: props.property.isExclusive ?? false,
	clickCard: true,
	videoShorts: hasShortsVideo(props.property)
}));

const {
	isFavorite,
	handleToggleFavorite,
	isValidData: isFavoriteValid
} = useToggleFavoriteSync(
	propertyId,
	commercialId,
	favoriteEventData,
	sectionName
);

const agentInfo = computed(() => props.property.agent ?? MOCK_AGENT);
const companyInfo = computed(() => props.property.company ?? MOCK_COMPANY);

const CARD_TYPE = 'shorts card';

// Preset do thumbnail compartilhado com o pôster do feed — URLs idênticas
// garantem cache hit na transição card → reel (ver reelThumbnailPreset.ts).

const { trackPropertyCardClicked } = useEvents();

const recommendationAnalyticsProperties = computed(() => {
	if (
		props.recommendationType !== 'similares' &&
		props.recommendationType !== 'fallback'
	) {
		return {};
	}

	return {
		[MixpanelProps.RECOMMENDATION_TYPE]: props.recommendationType
	};
});

function handleClick() {
	const payload = {
		[MixpanelProps.COMMERCIAL_ID]: props.property.commercialId,
		[MixpanelProps.DESTINATION_URL]: `/shorts/${props.property.commercialId}`,
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: sectionName.value,
		[MixpanelProps.CARD_POSITION]: props.cardPosition ?? props.index,
		[MixpanelProps.CARD_TYPE]: CARD_TYPE,
		[MixpanelProps.TAG_EXCLUSIVE]: props.property.isExclusive ?? false,
		[MixpanelProps.COMPANY_NAME]: props.property.company?.name ?? '',
		[MixpanelProps.AGENT_NAME]: props.property.agent?.name ?? '',
		...recommendationAnalyticsProperties.value
	};
	trackPropertyCardClicked(payload);
	emit('click', props.index);
}

// A reused instance rendering another property must be able to emit again —
// the engaged signal belongs to the card content, not to the component.
watch(
	() => props.property.commercialId,
	() => {
		clearTimeout(engagedTimer);
		hasEmittedEngaged = false;
	}
);

watch(
	() => isVideoPlaying.value,
	playing => {
		clearTimeout(engagedTimer);
		if (!playing || hasEmittedEngaged) return;
		engagedTimer = setTimeout(() => {
			hasEmittedEngaged = true;
			emit('engaged', props.index);
		}, ENGAGED_PREVIEW_MS);
	}
);

onUnmounted(() => clearTimeout(engagedTimer));
</script>

<template>
	<div
		ref="cardRef"
		class="video-card group relative aspect-9/14 h-full w-full cursor-pointer overflow-hidden bg-primary-light-4 transition-shadow duration-200"
		@click="handleClick"
		@pointerenter="handlePointerEnter"
	>
		<div
			:class="[
				'absolute inset-0 transition-[transform,opacity] duration-300',
				isVideoPlaying ? 'scale-105 opacity-0' : 'scale-100 opacity-100'
			]"
		>
			<NuxtImg
				v-if="props.thumbnailUrl"
				:src="props.thumbnailUrl"
				:alt="`Vídeo do imóvel ${property.commercialId}`"
				class="size-full object-cover"
				:width="REEL_THUMB_WIDTH"
				:height="REEL_THUMB_HEIGHT"
				:sizes="REEL_THUMB_SIZES"
				:format="REEL_THUMB_FORMAT"
				:quality="REEL_THUMB_QUALITY"
				:loading="props.index < props.imgEagerCount ? 'eager' : 'lazy'"
				:fetchpriority="props.index === 0 ? 'high' : undefined"
				:preload="
					props.imgPreloadCount > 0 && props.index < props.imgPreloadCount
				"
			/>
			<div v-else class="size-full bg-primary-light-4" />
		</div>

		<video
			v-if="isVisible"
			ref="videoRef"
			:class="[
				'pointer-events-none absolute inset-0 size-full object-cover transition-opacity duration-300',
				isVideoPlaying ? 'opacity-100' : 'opacity-0'
			]"
			:muted="isMuted"
			playsinline
			loop
		/>

		<div
			class="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,_rgba(0,0,0,0.3)_11.48%,_rgba(0,0,0,0)_31.75%,_rgba(0,0,0,0)_58.79%,_rgba(0,0,0,0.3)_79.07%)]"
		/>

		<VideoCardHeader
			v-model:is-favorite="isFavorite"
			:agent-info="agentInfo"
			:company-info="companyInfo"
			:property-id="property.id"
			:commercial-id="property.commercialId"
			:is-muted="isMuted"
			:is-favorite-valid="isFavoriteValid"
			:is-visible="shouldPlay"
			:hide-agent-info="false"
			@toggle-mute="handleMuteToggle"
			@toggle-favorite="handleToggleFavorite"
		/>

		<div
			v-if="!isVideoPlaying"
			class="absolute inset-0 z-50 flex items-center justify-center"
		>
			<div
				class="flex size-11 items-center justify-center rounded-full bg-black transition-all duration-400"
				:class="[isLoading ? 'scale-50 opacity-0' : 'scale-100 opacity-100']"
			>
				<IconMaterialSymbolsLightPlayArrow class="size-7 text-white" />
			</div>
		</div>

		<VideoCardLoadingBar v-if="isLoading && !isVideoPlaying" />

		<VideoCardFooter
			:formatted-price="formattedPrice"
			:formatted-area="formattedArea"
			:area-label="formattedArea ? 'm²' : ''"
			:rooms-info="roomsInfo"
			:parking-spots="property.parkingSpots"
			:region="propertyLocation"
			:city-state="cityState"
			:video-progress="videoProgress"
			:is-video-playing="isVideoPlaying"
			@seek="seekTo"
		/>
	</div>
</template>
